import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { cn } from '@worldcoo/ui/lib/utils';

/**
 * Percentages of the hand-off's 729x458 group, so the orbit scales as one piece. The export gives section
 * coordinates, so each value is the export's minus the group origin (79, 131).
 */
const ORBITS = [
	// A shared left tangent at x=127, so they read as ripples from the Delivery Hero node, not concentric circles.
	{ left: '6.6%', top: '6.1%', width: '93.4%', height: '93.9%' },
	{ left: '6.6%', top: '22.1%', width: '56.7%', height: '63.3%' },
	{ left: '6.6%', top: '37.8%', width: '20.2%', height: '31.9%' },
];

/** The plate is the export's, shown while the photo loads. Order was read visually: average colours swapped two. */
const BUBBLES = [
	// Square crops around each subject's measured centroid, so a plain cover fill has nothing left to steer.
	{ left: '26.7%', top: '17.2%', plate: 'bg-brand', src: '/redesign/about/glovo-bag.jpg' },
	{ left: '50.5%', top: '0%', plate: 'bg-ink', src: '/redesign/about/courier-handover.jpg' },
	{ left: '38.5%', top: '76.4%', plate: 'bg-olive', src: '/redesign/about/glovo-courier-park.jpg' },
	{ left: '83.0%', top: '79.3%', plate: 'bg-brand', src: '/redesign/about/courier-rain.jpg' },
];

function Node(props: { left: string; children: React.ReactNode; className?: string }) {
	return (
		<div
			style={{ left: props.left }}
			className={cn(
				'absolute top-[40.4%] flex h-[25.3%] w-[15.9%] items-center justify-center rounded-full bg-white',
				props.className,
			)}
		>
			{props.children}
		</div>
	);
}

export function DeliveryHero() {
	const { t } = useLingui();
	return (
		<section className="bg-linen relative overflow-hidden">
			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-12 px-6 py-16 md:py-20 xl:h-[720px] xl:gap-0 xl:px-0 xl:py-0">
				{/* First in the DOM for screen readers; the mobile hand-off puts the diagram above, so `order` moves it. */}
				<div className="order-2 flex w-full max-w-[443px] flex-col gap-2 xl:absolute xl:top-[256px] xl:left-[918px] xl:order-none">
					<h2 className="text-ink text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`Part of the Delivery Hero ecosystem`}
					</h2>
					{/* The export's #000000, unlike every other light-band body copy; shipped as drawn until design confirms. */}
					<p className="text-base leading-[1.5] text-black">
						{t`In 2023, Worldcoo was acquired by Delivery Hero and has been part of the Glovo family in Spain ever since. This partnership multiplies our technological and logistical capacity to take digital solidarity even further.`}
					</p>
				</div>

				<div className="relative order-1 aspect-[729/458] w-full xl:absolute xl:top-[131px] xl:left-[79px] xl:order-none xl:w-[729px]">
					{ORBITS.map((orbit) => (
						<div key={orbit.width} aria-hidden style={orbit} className="border-ink/25 absolute rounded-[50%] border" />
					))}

					<div
						aria-hidden
						className="border-ink/15 absolute top-[53.7%] left-[8.1%] w-[83.1%] border-t border-dashed"
					/>

					{BUBBLES.map((bubble) => (
						<div
							key={bubble.src}
							style={{ left: bubble.left, top: bubble.top }}
							// Decorative: the paragraph says it all, and four msgids would cost forty translations of ornament.
							aria-hidden
							className={cn('absolute h-[12.2%] w-[7.7%] overflow-hidden rounded-full', bubble.plate)}
						>
							{/* The `xl` size anchors the srcset; the box itself shrinks with the diagram. */}
							<Image
								src={bubble.src}
								alt=""
								height={56}
								aspectRatio={1}
								objectFit="cover"
								className="size-full object-cover!"
							/>
						</div>
					))}

					<Node left="0%">
						<img src="/redesign/about/delivery-hero.svg" alt="Delivery Hero" className="h-auto w-[75%]" />
					</Node>
					<Node left="45.0%">
						<img src="/redesign/about/glovo.svg" alt="Glovo" className="h-auto w-[63.8%]" />
					</Node>
					{/* The hand-off's lockup (same 1.78 aspect); the wordmark's #262626 is not `ink`, so it stays a literal. */}
					<Node left="81.3%">
						<WorldcooLogo className="h-auto w-1/2 text-[#262626]" accentClassName="text-brand" />
					</Node>
				</div>
			</div>
		</section>
	);
}
